import * as yup from 'yup';
import type { CreativeEntry } from 'types/shared';
import type {
  TwitterAdSettingsPayload,
  TwitterAdSettingsResponse,
  TwitterCampaignWithLineItems,
} from '../api/types';

export const TWITTER_STEP = {
  CREATIVES: 0,
  ACCOUNT: 1,
  CAMPAIGNS: 2,
  LINE_ITEMS: 3,
  ADS: 4,
} as const;

export const TWITTER_VALIDATION_MESSAGE = {
  creatives: 'At least one creative has to be selected',
  account: "Can't be empty",
  campaigns: 'At least one campaign has to be selected',
  lineItems: 'At least one ad group has to be selected',
  lineItemsPerCampaign: 'At least one ad group must be selected for each selected campaign that has ad groups with a flow',
  adSettings: 'Text and locale must be selected for every flow',
} as const;

export const twitterSchema = yup
  .object()
  .shape({
    currentStep: yup.number().default(0).optional(),
    app: yup.string().required("Can't be empty"),
    accountId: yup.string().default(''),
    selectedCreatives: yup.array().of(yup.string().required()).default([]),
    selectedCreativesOnly: yup.bool().default(false),
    filterCreativeByName: yup.array().of(yup.string().required()).default([]),
    selectedCampaigns: yup.array().of(yup.string().required()).default([]),
    selectedCampaignsOnly: yup.bool().default(false),
    filterCampaignsByName: yup.array().of(yup.string().required()).default([]),
    selectedLineItems: yup.array().of(yup.string().required()).default([]),
    filterLineItemsByName: yup.array().of(yup.string().required()).default([]),
    adSettings: yup.mixed<Record<string, TwitterAdSettingsPayload | undefined>>().default({}),
    $creativesRows: yup.mixed<CreativeEntry[]>().optional(),
    $lineItemsData: yup.mixed<TwitterCampaignWithLineItems[]>().optional(),
    $adSettingsData: yup.mixed<TwitterAdSettingsResponse>().optional(),
  })
  .test('step-validation', 'Step validation failed', function (value) {
    const currentStep = value.currentStep ?? 0;

    if (currentStep === TWITTER_STEP.CREATIVES && value.selectedCreatives.length === 0) {
      return this.createError({
        path: 'selectedCreatives',
        message: TWITTER_VALIDATION_MESSAGE.creatives,
      });
    }

    if (currentStep === TWITTER_STEP.ACCOUNT && !value.accountId) {
      return this.createError({
        path: 'accountId',
        message: TWITTER_VALIDATION_MESSAGE.account,
      });
    }

    if (currentStep === TWITTER_STEP.CAMPAIGNS && value.selectedCampaigns.length === 0) {
      return this.createError({
        path: 'selectedCampaigns',
        message: TWITTER_VALIDATION_MESSAGE.campaigns,
      });
    }

    if (currentStep === TWITTER_STEP.LINE_ITEMS) {
      if (value.selectedLineItems.length === 0) {
        return this.createError({
          path: 'selectedLineItems',
          message: TWITTER_VALIDATION_MESSAGE.lineItems,
        });
      }

      const lineItemsData = value.$lineItemsData;
      if (Array.isArray(lineItemsData)) {
        const selected = new Set(value.selectedLineItems);
        // A campaign whose line items all lack a parsed flow has nothing
        // selectable, so it cannot be required to have a selection.
        const everyCampaignCovered = value.selectedCampaigns.every((campaignId) => {
          const campaign = lineItemsData.find((entry) => entry.campaign_id === campaignId);
          if (!campaign) return true;

          const selectable = campaign.line_items.filter((lineItem) => !!lineItem.flow);
          if (!selectable.length) return true;

          return selectable.some((lineItem) => selected.has(lineItem.line_item_id));
        });

        if (!everyCampaignCovered) {
          return this.createError({
            path: 'selectedLineItems',
            message: TWITTER_VALIDATION_MESSAGE.lineItemsPerCampaign,
          });
        }
      }
    }

    if (currentStep === TWITTER_STEP.ADS) {
      const settingsData = value.$adSettingsData;
      if (!settingsData) return true;

      const adSettings = value.adSettings ?? {};
      const everyFlowSet = Object.keys(settingsData).every(
        (flow) => !!adSettings[flow]?.text_id && !!adSettings[flow]?.locale,
      );

      if (!everyFlowSet) {
        return this.createError({
          path: 'adSettings',
          message: TWITTER_VALIDATION_MESSAGE.adSettings,
        });
      }
    }

    return true;
  });

export type TwitterFormValues = yup.InferType<typeof twitterSchema>;
